import React from 'react';
import { Button, Typography, Spin, List, Tooltip, Popconfirm } from 'antd';
import { DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty'
const { Text } = Typography;

/**
 * ConversationSidebar - Minimal left sidebar with a text-only conversations list.
 *
 * @param {Object} props
 * @param {boolean} props.collapsed - Whether sidebar is collapsed
 * @param {Array} props.conversations - List of conversations
 * @param {boolean} props.loading - Loading state
 * @param {string} props.currentConversationId - Currently selected conversation ID
 * @param {Function} props.onSelectConversation - Handler for selecting a conversation
 * @param {Function} props.onDeleteConversation - Handler for deleting a conversation
 * @param {Function} [props.onSettings] - Optional settings click handler
 */
export default function ConversationSidebar({
  collapsed,
  conversations,
  loading,
  currentConversationId,
  onSelectConversation,
  onDeleteConversation,
  onSettings
}) {
  const { t } = useTranslation();
  const sidebarWidth = collapsed ? 0 : 240;

  return (
    <div
      style={{
        flex: `0 0 ${sidebarWidth}px`,
        width: sidebarWidth,
        borderRight: collapsed ? 'none' : '1px solid #ececec',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f5f5f3',
        transition: 'width 0.2s, flex-basis 0.2s',
        overflow: 'hidden'
      }}
    >
      {/* Conversations List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 8px 8px' }}>
        {isEmpty(conversations) && loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="small" />
          </div>
        ) : (
          <List
            dataSource={conversations}
            renderItem={(item) => {
              const isActive = currentConversationId === item.id;
              return (
                <div
                  className="conversation-item"
                  onClick={() => onSelectConversation(item)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    marginBottom: '2px',
                    backgroundColor: isActive ? '#e6e6e1' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'background-color 0.15s ease'
                  }}
                >
                  <Text
                    ellipsis
                    style={{
                      fontSize: '13px',
                      flex: 1,
                      color: isActive ? '#1a1a1a' : '#3a3a3a',
                      fontWeight: isActive ? 500 : 400
                    }}
                  >
                    {item.title || t('operator.ai_chat.untitled', 'Untitled')}
                  </Text>
                  <Popconfirm
                    title={t('common.confirm.delete', 'Are you sure you want to delete?')}
                    onConfirm={(e) => onDeleteConversation(e, item.id)}
                    onCancel={(e) => e.stopPropagation()}
                    okText={t('common.button.yes', 'Yes')}
                    cancelText={t('common.button.no', 'No')}
                  >
                    <Tooltip title={t('common.button.delete', 'Delete')}>
                      <Button
                        className="delete-btn"
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: '#8c8c8c', marginLeft: 4 }}
                      />
                    </Tooltip>
                  </Popconfirm>
                </div>
              );
            }}
            locale={{ emptyText: t('operator.ai_chat.no_conversations', 'No conversations yet') }}
          />
        )}
      </div>

      {/* Bottom settings gear */}
      {onSettings && (
        <div style={{ padding: '10px 12px', borderTop: '1px solid #ececec' }}>
          <Tooltip title={t('operator.menu.api_integration.ai_audit_logs', 'AI Audit Logs')}>
            <Button
              type="text"
              icon={<SettingOutlined style={{ fontSize: 16 }} />}
              onClick={onSettings}
              style={{ color: '#6b6b6b' }}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
}
